import axios, { type AxiosError, type AxiosInstance } from 'axios';
import qs from 'qs';

import { INVENTORY_V3_API_URL } from '@/config/env';
import { inventoryV3Request } from '@/config/msalConfig';

import { getToken, triggerReauthentication } from './utils';

export interface InventoryV3ProblemDetails {
  code?: string;
  detail?: string | null;
  error?: string | { code?: string; message?: string };
  instance?: string | null;
  status?: number | null;
  title?: string | null;
  type?: string | null;
  [key: string]: unknown;
}

export type InventoryV3ApiError = AxiosError<InventoryV3ProblemDetails> & {
  code?: string;
  correlationId?: string;
  durationMs?: number;
};

const attachInventoryV3AuthInterceptors = (api: AxiosInstance) => {
  api.interceptors.request.use(async (config) => {
    config.headers = config.headers ?? {};

    config.headers.Authorization = `Bearer ${await getToken(
      inventoryV3Request,
    )}`;
    return config;
  });

  api.interceptors.response.use(
    (response) => response,
    async (error: InventoryV3ApiError) => {
      if (error?.response?.status === 401) {
        try {
          await triggerReauthentication(inventoryV3Request);
        } catch (reauthenticationError) {
          console.error(reauthenticationError);
        }
      }

      if (
        error?.response?.status === 401 ||
        error?.response?.status === 403 ||
        error?.response?.status === 404 ||
        (error?.response?.status ?? 0) >= 500
      ) {
        const responseData = error.response?.data;
        const nestedError =
          responseData?.error && typeof responseData.error === 'object'
            ? responseData.error
            : null;
        const responseCode = responseData?.code ?? nestedError?.code;

        if (responseCode) {
          error.code = responseCode;
        }
      }

      return Promise.reject(error);
    },
  );
};

export const inventoryV3Api = axios.create({
  baseURL: INVENTORY_V3_API_URL,
  paramsSerializer: (params) =>
    qs.stringify(params, { arrayFormat: 'repeat', encode: false }),
});

attachInventoryV3AuthInterceptors(inventoryV3Api);
